/**
 * Custom hook for conversation detail data fetching and management
 * Integrates TanStack Query with Zustand store for seamless state management
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { conversationDetailQueryOptions } from '../api/conversations';
import { useConversationStore } from '../stores/conversationStore';
import { useChatStore } from '../stores/chatStore';
import { fetchUploadStatus } from '../api/uploads';

/**
 * Basic hook for conversation detail data fetching
 */
export function useConversationDetail(
  conversationId: string,
  overrides?: { refetchOnMount?: boolean | 'always'; refetchOnReconnect?: boolean | 'always' },
) {
  return useQuery({
    ...conversationDetailQueryOptions(conversationId),
    ...overrides,
  });
}

/**
 * Polls the dedicated upload_status endpoint while a file is processing.
 * Updates the chatStore when the status changes to 'complete' or 'failed'.
 */
export function useUploadStatusPoll(conversationId: string) {
  const queryClient = useQueryClient();
  const isFileProcessing = useChatStore(
    (state) => state.fileProcessingStatus[conversationId]?.status === 'processing',
  );

  // Track the previous processing state so we can detect false→true transitions
  // and clear stale query cache from a prior upload cycle.
  const prevIsProcessingRef = useRef(isFileProcessing);
  useEffect(() => {
    if (isFileProcessing && !prevIsProcessingRef.current) {
      // New processing cycle started — evict any cached 'complete'/'failed'
      // result so the poll starts fresh and the effect below doesn't
      // immediately override the 'processing' state with stale data.
      queryClient.removeQueries({ queryKey: ['uploadStatus', conversationId] });
    }
    prevIsProcessingRef.current = isFileProcessing;
  }, [isFileProcessing, conversationId, queryClient]);

  const statusQuery = useQuery({
    queryKey: ['uploadStatus', conversationId],
    queryFn: () => fetchUploadStatus(conversationId),
    enabled: !!conversationId && isFileProcessing,
    refetchInterval: isFileProcessing ? 2_000 : false,
    staleTime: 0,
  });

  useEffect(() => {
    if (!statusQuery.data || !conversationId) return;
    const serverStatus = statusQuery.data.status;
    if (serverStatus === 'complete' || serverStatus === 'failed') {
      // Guard against stale cache from a previous upload cycle: if the query
      // data was last updated before the current processing started, skip it —
      // the poll will refetch shortly with authoritative data.
      const entry = useChatStore.getState().fileProcessingStatus[conversationId];
      if (entry?.startedAt && statusQuery.dataUpdatedAt < entry.startedAt) {
        return;
      }
      useChatStore.getState().setFileProcessingStatus(conversationId, serverStatus);
    }
    // isFileProcessing is intentionally included so that if something re-sets
    // the store to 'processing' while polling already shows 'complete', the
    // effect re-evaluates immediately.  The startedAt guard above prevents the
    // stale data from incorrectly clearing a new processing cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusQuery.data?.status, statusQuery.dataUpdatedAt, conversationId, isFileProcessing]);
}

/**
 * Enhanced hook that integrates with chat store
 * Simplified to return query state directly without complex synchronization
 */
export function useConversationDetailWithStore(conversationId: string) {
  const query = useConversationDetail(conversationId);

  // Poll upload status via dedicated lightweight endpoint
  useUploadStatusPoll(conversationId);

  // Track current conversation ID for navigation
  const { setCurrentConversationId } = useConversationStore(useShallow((state) => ({
    setCurrentConversationId: state.setCurrentConversationId,
  })));

  // Only track conversation ID changes for navigation
  useEffect(() => {
    if (conversationId) {
      setCurrentConversationId(conversationId);
    }
  }, [conversationId, setCurrentConversationId]);

  // Resume polling when a load/reconnect finds an operation in progress.
  // Terminal states for a locally active operation come only from the dedicated
  // status endpoint or WebSocket. A conversation refetch can complete with the
  // previous operation's stale terminal status and must not end a new cycle.
  useEffect(() => {
    if (query.data?.file_processing_status && conversationId) {
      const serverStatus = query.data.file_processing_status;
      const localStatus = useChatStore.getState().getFileProcessingStatus(conversationId);
      if (serverStatus === 'processing' && localStatus !== 'processing') {
        useChatStore.getState().setFileProcessingStatus(conversationId, 'processing');
      }
    }
  }, [query.data?.file_processing_status, conversationId]);

  return query;
}
